/**
 * Compact honesty control: icon + hover of what this page can and cannot do.
 */

import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { operationsHonestyCopy } from './pageBind';

interface OperationsHonestyHintProps {
  pathname: string;
}

export const OperationsHonestyHint: React.FC<OperationsHonestyHintProps> = ({ pathname }) => {
  const { translateWithFallback } = useLanguage();
  const copy = operationsHonestyCopy(pathname);
  const can = translateWithFallback(copy.canKey, copy.canFallback);
  const cannot = translateWithFallback(copy.cannotKey, copy.cannotFallback);
  const boundAs = translateWithFallback(copy.boundAsKey, copy.boundAsFallback);
  const label = translateWithFallback(
    'operations.pane.honestyAria',
    'What this page can and cannot do'
  );

  return (
    <div className="relative group shrink-0">
      <button
        type="button"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:opacity-90"
        aria-label={label}
        title={label}
      >
        <InformationCircleIcon className="h-4 w-4" aria-hidden />
      </button>
      <div
        role="tooltip"
        className="absolute right-0 top-full z-30 hidden w-72 group-hover:block group-focus-within:block p-2.5 text-xs leading-snug rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] shadow-sm"
      >
        <p className="font-semibold mb-1">{boundAs}</p>
        <p className="text-[10px] uppercase tracking-wide opacity-70">
          {translateWithFallback('operations.pane.honestyCan', 'Can')}
        </p>
        <p className="mt-0.5 mb-1.5">{can}</p>
        <p className="text-[10px] uppercase tracking-wide opacity-70">
          {translateWithFallback('operations.pane.honestyCannot', 'Cannot')}
        </p>
        <p className="mt-0.5">{cannot}</p>
      </div>
    </div>
  );
};
